import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { getStationHistoricalById } from '../store/reducers/fmAPI.js';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import loadable from '@loadable/component';

import Accordion from 'react-bootstrap/Accordion';

import { BoxArrowLeft } from 'react-bootstrap-icons';

import StationLinker from '../components/StationLinker.js';

import StationNameRenderer from '../components/StationNameRenderer.js';
import MapViewHeaderComponent from '../components/MapViewHeaderComponent.js'
import Loading from '../components/Loading.js';
import StationFloodEventLinker from '../components/StationFloodEventLinker.js'

import CommonHeaders from '../components/helmet-headers/CommonHeaders.js';
import Breadcrumbs, { CRUMBS } from '../components/helmet-headers/Breadcrumbs.js';

const AdComponent = loadable(() => import('../components/AdComponent.js'));
const FloodEventExplainer = loadable(() => import('../components/FloodEventExplainer.js'));

function StationHistoricalView() {
	const params = useParams();
	const dispatch = useDispatch();
	const location = useLocation();
	const { stationsHistorical } = useSelector(state => state.fmAPI);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location]);

	useEffect(() => {
		dispatch(getStationHistoricalById(params.stationId));
	}, [dispatch, params.stationId]);


	if(stationsHistorical && stationsHistorical[params.stationId]) {
		let station = stationsHistorical[params.stationId];

		let computedStationName = station && <StationNameRenderer stationName={station.stationName} qualifierType={'Stage'} />;
		
		let riverName = station && station.riverName;
		let stationName = station && station.stationName;
		let townName = station && station.townName;

		let computedTitle = null;
		let townNameTitleSuffix = !!townName ? " in " + townName : "";
		if(stationName) {
			computedTitle = "Historical Flooding Records :: " + stationName + " station" + townNameTitleSuffix;
			if(riverName) {
				computedTitle = "Historical Flooding Records :: " + riverName + " at the " + stationName + " station" + townNameTitleSuffix;
			}
		} else if(riverName) {
			computedTitle = "Historical Flooding Records :: " + riverName + townNameTitleSuffix;
		}


		let metaDescription = "A list of historical flood records as measured by the " + station.stationName + " station";
		if(station.riverName) {
			metaDescription = "A list of historical " + station.riverName + " flood records, as measured by the " + station.stationName + " station";
		}
		if(station.placeName) {
			metaDescription = metaDescription + ", in " + station.placeName;
		}
		metaDescription = metaDescription + ".";
		

		return <div>
			<CommonHeaders
				title={computedTitle}
				description={metaDescription}
				canonicalUrlPath={"/stations/" + (station.urlSlug ? station.urlSlug : station.stationId) + "/historical"}
				canonicalDefaultWebsite="UK"
				ogType="place"
				lat={station.stationLat}
				lng={station.stationLng}
				townName={station.placeName}
			/>
			<Breadcrumbs
				builtinCrumbs={[CRUMBS.HOME, CRUMBS.DATA_EXPLORER]}
				leafCrumbs={[{
					name: "The " + stationName + " station",
					path: "/stations/" + (station.urlSlug ? station.urlSlug : station.stationId)
				}, {
					name: "Historical Records",
					path: "/stations/" + (station.urlSlug ? station.urlSlug : station.stationId) + "/historical"
				}]}
			/>
			<Row className="screen-bottom-border">
				<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-left">&nbsp;</Col>
				<Col xs={12} lg={10} xl={8}>
					<MapViewHeaderComponent
						key={'Stage' + params.stationId}
						lat={station.stationLat}
						lng={station.stationLng}
						label={computedStationName ? <span>Historical Flooding Records :: {riverName ? riverName + " at " : ""}{computedStationName}</span> : "Unknown Measuring Station"}
					/>
					<Row>
						<Col xs={12}>
							<StationLinker stationId={station.stationId} urlSlug={station.urlSlug}>
								<Button variant="dark" size="sm">
									<b><BoxArrowLeft /> Back to Current Measurements</b>
								</Button>
							</StationLinker>
						</Col>
					</Row>
					{(station.metadata && !station.metadata.floodEventCount) ? <Row>
						<Col xs={12}><hr /><b><i>Sorry, we don't have any historical records for this station.</i></b><hr /></Col>
					</Row> : null}
					{(station.metadata.floodEventCount && station.metadata.floodEventCount > 0 && <Row>
						<Col xs={12}>
							<span style={{fontSize: "0.8em"}}>
								We have <b><i>{station.metadata.floodEventCount}</i></b> historical flood event records available for this station. These date back to <b><i>{station.metadata.earliestFloodEventTimeFormatted}</i></b>.<br />
							</span>
						</Col>
						{station.metadata.typicalRangeHigh && <Col xs={12}>
							<FloodEventExplainer typicalRangeHigh={station.metadata.typicalRangeHigh} />
						</Col>}
					</Row>) || null}
					{(station.metadata.floodEventCount && station.metadata.floodEventCount > 0 && <Row>
						<Col xs={12} className="bg-dark text-white">
							<h4 className="hX-blendin"><b>Notable Flood Records</b></h4>
						</Col>
					</Row>) || null}
					{(station.metadata.floodEventCount && station.metadata.floodEventCount > 0 && <Row>
						{station.floodEventsNotable.map(function(ev, ix) {
							return <Col xs={12}>
								<StationFloodEventLinker stationId={station.stationId} urlSlug={station.urlSlug} startTimestampFormatted={ev.startTimestampFormatted} endTimestampFormatted={ev.endTimestampFormatted} entryHeightMax={ev.entryHeightMax} displayIndex={(ix+1)} highestOnRecord={ix===0} />
							</Col>;
						})}
					</Row>) || null}
					{process.env.REACT_APP_ENABLE_GOOGLE_ADS_ON_HISTORIC === "true" ? <Row style={{marginTop: "3px"}}>
						<Col xs={12}>
							<AdComponent
							  client="ca-pub-6790224688210339"
							  slot="6130981811" // Replace this with your actual data-ad-slot value
							  format="fluid"
							  layoutKey="-fb+5w+4e-db+86" // Adding a new prop for data-ad-layout-key
							  responsive={true} // Assuming you want it responsive; adjust as necessary
							/>
						</Col>
					</Row> : null}
					{(station.metadata.floodEventCount && station.metadata.floodEventCount > 0 && <Row style={{marginTop: "3px"}}>
						<Col xs={12} className="bg-dark text-white">
							<h4 className="hX-blendin"><b>Extended Records</b></h4>
						</Col>
					</Row>) || null}
					{(station.metadata.floodEventCount && station.metadata.floodEventCount > 0 && <Accordion>
						{Object.keys(station.floodEvents).map(function(yearOfEvent) {
							let thisYearEvents = station.floodEvents[yearOfEvent];

							return <Accordion.Item eventKey={yearOfEvent}>
								<Accordion.Header>Year: {yearOfEvent} ({thisYearEvents.length})</Accordion.Header>
								<Accordion.Body>
									{thisYearEvents.map(function(ev) {
										try {
											return <StationFloodEventLinker stationId={station.stationId} startTimestampFormatted={ev.startTimestampFormatted} endTimestampFormatted={ev.endTimestampFormatted} entryHeightMax={ev.entryHeightMax} displayIndex={false} highestOnRecord={ev.entryHeightMax===station.floodEventsNotable[0].entryHeightMax} />
										} catch(error) {
											console.error(error);
										}
										return <StationFloodEventLinker stationId={station.stationId} startTimestampFormatted={ev.startTimestampFormatted} endTimestampFormatted={ev.endTimestampFormatted} entryHeightMax={ev.entryHeightMax} displayIndex={false} highestOnRecord={false} />
									})}
								</Accordion.Body>
							</Accordion.Item>
						})}
					</Accordion>) || null}
				</Col>
				<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-right">&nbsp;</Col>
			</Row>
		</div>;

	} else {
		return <div><Loading /></div>;
	}


}







export default StationHistoricalView;



// /**
// if(areas.length > 10 && process.env.REACT_APP_ENABLE_GOOGLE_ADS_ON_FLOODNAV === "true") {
// 		/*
// 		<ins class="adsbygoogle"
//      style="display:block"
//      data-ad-format="fluid"
//      data-ad-layout-key="-fb+5w+4e-db+86"
//      data-ad-client="ca-pub-6790224688210339"
//      data-ad-slot="6130981811"></ins>*/
// 		areas.splice(
// 			9,
// 			0,
// 			<Row>
// 				<Col xs={12}>
// 				<AdComponent
// 					  client="ca-pub-6790224688210339"
// 					  slot="6130981811" // Replace this with your actual data-ad-slot value
// 					  format="fluid"
// 					  layoutKey="-fb+5w+4e-db+86" // Adding a new prop for data-ad-layout-key
// 					  responsive={true} // Assuming you want it responsive; adjust as necessary
// 					/>
// 				</Col>
// 			</Row>
// 		);
// 	}

// 	return (
// 		<div>{areas}</div>
// 	);*/




